import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PhoneCall, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PayLaterCalculator } from "@/components/booking/PayLaterCalculator";
import { JR_PACKAGES, SIBLING_DISCOUNT } from "./juniorData";
import { SessionPicker, sessionLabel } from "./SessionPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { parent_name: "", parent_mobile: "", email: "", suburb: "", student_name: "", student_age: "", experience: "", genres: "" };
const EMPTY_SIB = { student_name: "", student_age: "", experience: "", genres: "" };
const AGES = [10, 11, 12, 13, 14, 15, 16, 17];
const EXPERIENCE = ["Complete beginner", "Some experience", "Experienced"];
const inputCls = "bg-dream-bg border-white/15 text-white h-11";
const selectCls = "w-full h-11 px-3 rounded-md bg-dream-bg border border-white/15 text-white";
const blank = (n) => Array.from({ length: n }, () => ({ date: "", hour: null }));
const fmtDay = (d) => new Date(`${d}T00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-white/70 text-sm">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Toggle = ({ options, value, onChange, testPrefix }) => (
  <div className="flex gap-1 p-1 rounded-full bg-black/40 border border-white/10">
    {options.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} className={`flex-1 py-2.5 px-3 rounded-full text-sm font-semibold transition-colors ${value === v ? "bg-lime-300 text-black" : "text-white/60 hover:text-white"}`} data-testid={`${testPrefix}-${v}`}>
        {label}
      </button>
    ))}
  </div>
);

const StudentFields = ({ data, onChange, prefix, who = "Student" }) => {
  const set = (k) => (e) => onChange({ ...data, [k]: e.target.value });
  return (
    <>
      <Field label={`${who} name`}><Input value={data.student_name} onChange={set("student_name")} className={inputCls} data-testid={`${prefix}-student-name`} /></Field>
      <Field label={`${who} age`}>
        <select value={data.student_age} onChange={set("student_age")} className={selectCls} data-testid={`${prefix}-student-age`}>
          <option value="">Select age</option>
          {AGES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </Field>
      <Field label="Experience">
        <select value={data.experience} onChange={set("experience")} className={selectCls} data-testid={`${prefix}-experience`}>
          <option value="">Select</option>
          {EXPERIENCE.map((x) => <option key={x}>{x}</option>)}
        </select>
      </Field>
      <Field label="Favourite music genres"><Input value={data.genres} onChange={set("genres")} placeholder="e.g. House, Hip-Hop, Techno" className={inputCls} data-testid={`${prefix}-genres`} /></Field>
    </>
  );
};

const SessionList = ({ sessions, setSessions, others, testPrefix }) => (
  <div className="space-y-3">
    {sessions.map((s, i) => (
      <SessionPicker
        key={i}
        index={i}
        value={s}
        testPrefix={testPrefix}
        takenDates={sessions.filter((_, j) => j !== i).map((x) => x.date)}
        takenPairs={others.filter((x) => x.date && x.hour !== null).map((x) => [x.date, x.hour])}
        onChange={(v) => setSessions(sessions.map((x, j) => (j === i ? v : x)))}
      />
    ))}
  </div>
);

export const JuniorBookingForm = ({ packageId, setPackageId }) => {
  const pkg = JR_PACKAGES.find((p) => p.id === packageId);
  const [sessions, setSessions] = useState([]);
  const [sibSessions, setSibSessions] = useState(blank(4));
  const [hasSibling, setHasSibling] = useState(false);
  const [sibling, setSibling] = useState(EMPTY_SIB);
  const [bookedBy, setBookedBy] = useState("parent");
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSessions(blank(pkg.sessions));
    if (pkg.sessions === 1) setHasSibling(false);
  }, [pkg.sessions]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isStudent = bookedBy === "student";
  const sibActive = hasSibling && pkg.sessions > 1;
  const sibPrice = pkg.price * (1 - SIBLING_DISCOUNT);
  const total = pkg.price + (sibActive ? sibPrice : 0);
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null);
  useEffect(() => setVoucher(null), [packageId]);
  const voucherUsed = voucher ? Math.min(voucher.balance, total) : 0;
  const due = Math.max(0, total - voucherUsed);

  const applyVoucher = async () => {
    if (!voucherInput.trim()) return;
    try {
      const r = await axios.get(`${API}/vouchers/${encodeURIComponent(voucherInput.trim().toUpperCase())}/check`, { params: { service_id: packageId } });
      setVoucher(r.data);
      toast.success(`Voucher applied: $${r.data.balance.toFixed(2)} available`);
    } catch (e) {
      setVoucher(null);
      toast.error(e.response?.data?.detail || "Voucher not found");
    }
  };

  const validate = () => {
    const groups = sibActive ? [sessions, sibSessions] : [sessions];
    if (groups.some((g) => g.some((s) => !s.date || s.hour === null))) return "Please choose a date and time for every session";
    if (groups.some((g) => new Set(g.map((s) => s.date)).size !== g.length)) return "Each child's sessions need different days";
    if (Object.values(form).some((v) => !String(v).trim())) return "Please fill in all the details";
    if (sibActive && Object.values(sibling).some((v) => !String(v).trim())) return "Please fill in the sibling's details";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email";
    if (isStudent && Number(form.student_age) < 15) return "Students under 15 must be booked by a parent or guardian";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const [first, ...rest] = sessions;
      const slot = (s, extra = {}) => ({ date: s.date, time_slots: [s.hour, s.hour + 1], ...extra });
      const res = await axios.post(`${API}/bookings`, {
        full_name: isStudent ? form.student_name : form.parent_name,
        artist_name: sibActive ? `${form.student_name} & ${sibling.student_name}` : form.student_name,
        email: form.email.trim(),
        phone: form.parent_mobile,
        service_id: pkg.id,
        booking_date: first.date,
        time_slots: [first.hour, first.hour + 1],
        extra_sessions: [...rest.map((s) => slot(s)), ...(sibActive ? sibSessions.map((s) => slot(s, { student: 2 })) : [])],
        junior_details: {
          ...form,
          student_age: Number(form.student_age),
          booked_by: bookedBy,
          ...(sibActive ? { sibling: { ...sibling, student_age: Number(sibling.student_age) } } : {}),
        },
      });
      const checkout = await axios.post(`${API}/checkout/create`, { booking_id: res.data.id, origin_url: window.location.origin, ...(voucher ? { voucher_code: voucher.code } : {}) });
      window.location.href = checkout.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed, please try again");
      setLoading(false);
    }
  };

  const picked = (list) => list.filter((s) => s.date && s.hour !== null);

  return (
    <section id="jr-book" className="py-20 md:py-28 bg-dream-surface scroll-mt-20" data-testid="jr-booking">
      <div className="max-w-3xl mx-auto px-6">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-lime-300 mb-4">Book now</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">Lock in their spot.</h2>
        <p className="text-white/60 mt-3">Sessions run 9AM–9PM, 7 days a week, from 19 December 2026 through the summer holidays (to 24 January 2027) and all year round after that.</p>

        <div className="mt-10 space-y-8 rounded-3xl p-6 md:p-8 bg-dream-bg/60 border border-white/10">
          <Toggle options={JR_PACKAGES.map((p) => [p.id, `${p.name} · $${p.price}`])} value={packageId} onChange={setPackageId} testPrefix="jr-pkg" />

          <div className="space-y-3">
            <p className="text-white font-semibold">{pkg.sessions > 1 ? "Choose all 4 sessions (1.5 hrs each)" : "Choose your session (1.5 hrs)"}</p>
            <SessionList sessions={sessions} setSessions={setSessions} others={sibActive ? sibSessions : []} testPrefix="jr-session" />
          </div>

          <div className="space-y-4">
            <p className="text-white font-semibold">Who&apos;s booking?</p>
            <Toggle options={[["parent", "Parent / guardian"], ["student", "Student (15–17)"]]} value={bookedBy} onChange={setBookedBy} testPrefix="jr-bookedby" />
            {isStudent && <p className="text-yellow-300 text-xs flex gap-1.5"><PhoneCall className="w-3.5 h-3.5 flex-shrink-0" /> We&apos;ll call your parent or guardian to verify before your first lesson.</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Parent / guardian name"><Input value={form.parent_name} onChange={set("parent_name")} className={inputCls} data-testid="jr-parent-name" /></Field>
              <Field label="Parent / guardian mobile"><Input type="tel" value={form.parent_mobile} onChange={set("parent_mobile")} className={inputCls} data-testid="jr-parent-mobile" /></Field>
              <Field label={isStudent ? "Your email" : "Email"}><Input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="jr-email" /></Field>
              <Field label="Suburb"><Input value={form.suburb} onChange={set("suburb")} className={inputCls} data-testid="jr-suburb" /></Field>
              <StudentFields data={form} onChange={setForm} prefix="jr" />
            </div>
            <p className="text-white/50 text-xs">Once booked, we&apos;ll reach out to collect their favourite songs and have a quick chat with the parent.</p>
          </div>

          {pkg.sessions > 1 && !hasSibling && (
            <button type="button" onClick={() => setHasSibling(true)} className="w-full p-5 rounded-2xl border-2 border-dashed border-pink-400/60 bg-pink-500/10 hover:bg-pink-500/15 text-left transition-colors" data-testid="jr-add-sibling-btn">
              <span className="flex items-center gap-2 text-white font-semibold"><UserPlus className="w-5 h-5 text-pink-400" /> Add a sibling: save 15% on their 4-Week Starter</span>
              <span className="block text-white/60 text-sm mt-1">Their package is ${sibPrice.toFixed(0)} instead of ${pkg.price}. They pick their own 4 sessions.</span>
            </button>
          )}
          {sibActive && (
            <div className="space-y-4 p-5 rounded-2xl border border-pink-400/50 bg-pink-500/[0.07]" data-testid="jr-sibling-section">
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold">Sibling <span className="ml-2 px-2 py-0.5 rounded-full bg-pink-500 text-white text-xs font-bold">15% OFF</span></p>
                <button type="button" onClick={() => setHasSibling(false)} className="text-white/50 hover:text-white text-sm flex items-center gap-1" data-testid="jr-remove-sibling-btn"><X className="w-4 h-4" /> Remove</button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4"><StudentFields data={sibling} onChange={setSibling} prefix="jr-sib" who="Sibling's" /></div>
              <p className="text-white/80 text-sm font-semibold">Sibling&apos;s 4 sessions</p>
              <SessionList sessions={sibSessions} setSessions={setSibSessions} others={sessions} testPrefix="jr-sib-session" />
            </div>
          )}

          <div className="rounded-2xl p-5 bg-white/[0.03] border border-white/10 space-y-2" data-testid="jr-summary">
            <div className="flex justify-between text-white"><span>{pkg.name}{sibActive ? ` · ${form.student_name || "Child 1"}` : ""}</span><span className="font-mono">${pkg.price.toFixed(2)}</span></div>
            <ul className="space-y-1 text-white/55 text-sm">{picked(sessions).map((s, i) => <li key={i}>{fmtDay(s.date)} · {sessionLabel(s.hour)}</li>)}</ul>
            {sibActive && (
              <>
                <div className="flex justify-between text-white pt-2"><span>{pkg.name} · {sibling.student_name || "Sibling"} (15% off)</span><span className="font-mono">${sibPrice.toFixed(2)}</span></div>
                <ul className="space-y-1 text-white/55 text-sm">{picked(sibSessions).map((s, i) => <li key={i}>{fmtDay(s.date)} · {sessionLabel(s.hour)}</li>)}</ul>
                <p className="text-pink-300 text-sm" data-testid="jr-sibling-saving">Sibling saving: ${(pkg.price - sibPrice).toFixed(2)}</p>
              </>
            )}
            <div className="flex justify-between text-white font-bold pt-2 border-t border-white/10"><span>Total</span><span className="font-mono" data-testid="jr-summary-total">${total.toFixed(2)}</span></div>
            {voucher && (
              <>
                <div className="flex justify-between text-lime-300 text-sm"><span>Gift voucher {voucher.code}</span><span className="font-mono" data-testid="jr-voucher-deduction">-${voucherUsed.toFixed(2)}</span></div>
                <div className="flex justify-between text-white font-bold"><span>To pay</span><span className="font-mono" data-testid="jr-amount-due">${due.toFixed(2)}</span></div>
              </>
            )}
          </div>
          <div className="flex gap-2" data-testid="jr-voucher-box">
            <Input value={voucherInput} onChange={(e) => setVoucherInput(e.target.value)} placeholder="Have a gift voucher? DMRG-XXXX-XXXX" className={`${inputCls} uppercase`} data-testid="jr-voucher-input" />
            {voucher ? (
              <Button type="button" variant="outline" onClick={() => { setVoucher(null); setVoucherInput(""); }} className="border-white/20 text-white bg-transparent rounded-full h-11" data-testid="jr-voucher-remove-btn">Remove</Button>
            ) : (
              <Button type="button" onClick={applyVoucher} className="bg-pink-500 hover:bg-pink-400 text-white rounded-full h-11 px-6" data-testid="jr-voucher-apply-btn">Apply</Button>
            )}
          </div>
          <PayLaterCalculator total={due} />
          <Button onClick={submit} disabled={loading} className="w-full bg-lime-300 hover:bg-lime-200 text-black rounded-full py-7 text-base font-bold" data-testid="jr-pay-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : due === 0 ? "Book with Gift Voucher" : `Book & Pay $${due.toFixed(2).replace(/\.00$/, "")}`}
          </Button>
        </div>
      </div>
    </section>
  );
};
